import React, { useState, useEffect } from 'react';
import { 
  Trophy, Users, TrendingUp, Target, Activity, 
  BarChart3, Calendar, Star, Crown, Zap,
  Award, User, DollarSign, TrendingDown,
  Crosshair, Shield, Flame, AlertTriangle,
  Swords, ThumbsUp, ThumbsDown, Gamepad2
} from 'lucide-react';

import StandingsHistoryChart from './StandingsHistoryChart';
import PointScoringHistoryChart from './PointScoringHistoryChart';
import PointsAgainstHistoryChart from './PointsAgainstHistoryChart';
import MarginOfVictoryChart from './MarginOfVictoryChart';
import HeadToHeadPerformanceChart from './HeadToHeadPerformanceChart';
import TopInvestmentsChart from './TopInvestmentsChart';
import DraftTendenciesChart from './DraftTendenciesChart';
import LegacyScoreChart from './LegacyScoreChart';
import LegacyRankChart from './LegacyRankChart';
import IndividualTeamsLegacyChart from './IndividualTeamsLegacyChart';

const ManagerCards = ({ h2hData = [], finalData = [], draftData = [], legacyData = [], loading = false, error = null }) => {
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add CSS animations to document head for premium lighting effects
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes heroGlow {
        0%, 100% { 
          filter: drop-shadow(0 0 40px rgba(255,255,255,0.2)) drop-shadow(0 0 80px rgba(147,197,253,0.3)) brightness(1.05) contrast(1.1);
        }
        50% { 
          filter: drop-shadow(0 0 60px rgba(255,255,255,0.4)) drop-shadow(0 0 120px rgba(147,197,253,0.5)) drop-shadow(0 0 80px rgba(168,85,247,0.3)) brightness(1.1) contrast(1.15);
        }
      }
      
      @keyframes lightSweep {
        0% { transform: translateX(-100%) skewX(-12deg); }
        100% { transform: translateX(100%) skewX(-12deg); }
      }
      
      .bg-gradient-radial {
        background: radial-gradient(circle, var(--tw-gradient-stops));
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Alphabetical order and random default selection (excluding Sam)
  const managers = ['Dan', 'Jake', 'Joey', 'Karp', 'Kevin', 'Lyons', 'Mills', 'Nipples', 'Oster', 'Raffy', 'Sam', 'Scott', 'Shuds'];
  const managersExcludingSam = managers.filter(manager => manager !== 'Sam');
  
  // Random default manager (excluding Sam)
  const getRandomManager = () => {
    const randomIndex = Math.floor(Math.random() * managersExcludingSam.length);
    return managersExcludingSam[randomIndex];
  };
  
  const [selectedManager, setSelectedManager] = useState(getRandomManager());

  // Calculate comprehensive manager statistics
  const calculateManagerStats = (manager) => {
    if (!h2hData.length || !finalData.length) {
      return null;
    }

    const managerH2H = h2hData.filter(row => row.Manager1 === manager);
    const managerFinal = finalData.filter(row => row.Owner === manager);

    // Get current/most recent periods for recency checks
    const allYears = [...new Set(finalData.map(row => row.Year))].sort((a, b) => b - a);
    const mostRecentYear = allYears[0]; // Most recent completed year
    
    // For games, find the most recent completed week
    const allWeeks = managerH2H.map(row => ({ year: row.Year, week: row.Week }))
      .filter(item => item.year && item.week)
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.week - a.week;
      });
    const mostRecentWeek = allWeeks[0]; // Most recent completed week

    // Get most recent team name
    const mostRecentTeamData = managerFinal.length > 0 ? 
      managerFinal.reduce((latest, current) => 
        current.Year > latest.Year ? current : latest
      ) : null;
    const currentTeamName = mostRecentTeamData?.Team || 'Fantasy Manager';

    // Overall record and averages for quick preview
    const totalWins = managerH2H.reduce((sum, row) => sum + (row.IsWin || 0), 0);
    const totalLosses = managerH2H.reduce((sum, row) => sum + (row.IsLoss || 0), 0);
    const totalGames = totalWins + totalLosses;
    const winPercentage = totalGames > 0 ? (totalWins / totalGames * 100).toFixed(1) : '0.0';
    const avgStanding = managerFinal.length > 0 ? (managerFinal.reduce((sum, row) => sum + (row.Standings || 0), 0) / managerFinal.length).toFixed(1) : '0.0';

    // Championship data from Final Data (Standings = 1)
    const championshipSeasons = managerFinal.filter(row => row.Standings === 1);
    const championships = championshipSeasons.length;
    const championshipYears = championshipSeasons.map(row => row.Year).sort();
    const hasNewChampionship = championshipYears.includes(mostRecentYear);
    
    // Championship appearances - could be top 2 finishes or need additional logic
    // For now, let's use top 2 finishes as a proxy
    const champAppearanceSeasons = managerFinal.filter(row => row.Standings <= 2);
    const champAppearances = champAppearanceSeasons.length;
    const champAppearanceYears = champAppearanceSeasons.map(row => row.Year).sort();
    const hasNewChampAppearance = champAppearanceYears.includes(mostRecentYear);

    // Sacko data - need to find last place for each year
    const years = [...new Set(finalData.map(row => row.Year))];
    const sackoSeasons = [];
    const sackoAppearanceSeasons = [];

    years.forEach(year => {
      const yearData = finalData.filter(row => row.Year === year);
      const maxStanding = Math.max(...yearData.map(row => row.Standings));
      const minStanding = Math.min(...yearData.map(row => row.Standings));
      
      // Sacko winner (last place)
      const sackoWinner = yearData.find(row => row.Standings === maxStanding);
      if (sackoWinner && sackoWinner.Owner === manager) {
        sackoSeasons.push(year);
      }
      
      // Sacko appearance (bottom 2)
      const bottomTwo = yearData.filter(row => row.Standings >= maxStanding - 1);
      if (bottomTwo.some(row => row.Owner === manager)) {
        sackoAppearanceSeasons.push(year);
      }
    });

    const sackos = sackoSeasons.length;
    const sackoYears = sackoSeasons.sort();
    const hasNewSacko = sackoYears.includes(mostRecentYear);
    
    const sackoAppearances = sackoAppearanceSeasons.length;
    const sackoAppearanceYears = sackoAppearanceSeasons.sort();
    const hasNewSackoAppearance = sackoAppearanceYears.includes(mostRecentYear);

    // Playoff appearances - count distinct years where manager played in playoffs
    const playoffAppearances = [...new Set(managerH2H.filter(row => row.Type === 'Playoffs').map(row => row.Year))].length;
    const seasonsInLeague = [...new Set(managerH2H.map(row => row.Year))].length;
    
    // Playoff victories - count wins in playoff games
    const playoffVictories = managerH2H.filter(row => row.Type === 'Playoffs' && row.IsWin === 1).length;
    const playoffGames = managerH2H.filter(row => row.Type === 'Playoffs').length;

    // Season records
    const bestSeason = managerFinal.length > 0 ? Math.min(...managerFinal.map(row => row.Standings || 999)) : 0;
    const bestSeasonYears = managerFinal.filter(row => row.Standings === bestSeason).map(row => row.Year).sort();
    const hasNewBestSeason = bestSeasonYears.includes(mostRecentYear);
    
    const worstSeason = managerFinal.length > 0 ? Math.max(...managerFinal.map(row => row.Standings || 0)) : 0;
    const worstSeasonYears = managerFinal.filter(row => row.Standings === worstSeason).map(row => row.Year).sort();
    const hasNewWorstSeason = worstSeasonYears.includes(mostRecentYear);

    const highestSeasonPF = managerFinal.length > 0 ? Math.max(...managerFinal.map(row => row.PF || 0)) : 0;
    const highestSeasonPFYears = managerFinal.filter(row => row.PF === highestSeasonPF).map(row => row.Year).sort();
    const hasNewHighestSeasonPF = highestSeasonPFYears.includes(mostRecentYear);
    
    const lowestSeasonPF = managerFinal.length > 0 ? Math.min(...managerFinal.map(row => row.PF || 0)) : 0;
    const lowestSeasonPFYears = managerFinal.filter(row => row.PF === lowestSeasonPF).map(row => row.Year).sort();
    const hasNewLowestSeasonPF = lowestSeasonPFYears.includes(mostRecentYear);

    const mostSeasonWins = managerFinal.length > 0 ? Math.max(...managerFinal.map(row => row.Wins || 0)) : 0;
    const mostSeasonWinsYears = managerFinal.filter(row => row.Wins === mostSeasonWins).map(row => row.Year).sort();
    const hasNewMostSeasonWins = mostSeasonWinsYears.includes(mostRecentYear);
    
    const fewestSeasonWins = managerFinal.length > 0 ? Math.min(...managerFinal.map(row => row.Wins || 0)) : 0;
    const fewestSeasonWinsYears = managerFinal.filter(row => row.Wins === fewestSeasonWins).map(row => row.Year).sort();
    const hasNewFewestSeasonWins = fewestSeasonWinsYears.includes(mostRecentYear);

    const mostSeasonMoves = managerFinal.length > 0 ? Math.max(...managerFinal.map(row => row.Moves || 0)) : 0;
    const mostSeasonMovesYears = managerFinal.filter(row => row.Moves === mostSeasonMoves).map(row => row.Year).sort();
    
    const fewestSeasonMoves = managerFinal.length > 0 ? Math.min(...managerFinal.map(row => row.Moves || 0)) : 0;
    const fewestSeasonMovesYears = managerFinal.filter(row => row.Moves === fewestSeasonMoves).map(row => row.Year).sort();

    // Single game records
    const highScore = managerH2H.length > 0 ? Math.max(...managerH2H.map(row => row.PF || 0)) : 0;
    const highScoreGame = managerH2H.find(row => row.PF === highScore);
    const hasNewHighScore = highScoreGame && mostRecentWeek && 
      highScoreGame.Year === mostRecentWeek.year && highScoreGame.Week === mostRecentWeek.week;
    
    const lowScore = managerH2H.length > 0 ? Math.min(...managerH2H.map(row => row.PF || 0)) : 0;
    const lowScoreGame = managerH2H.find(row => row.PF === lowScore);
    const hasNewLowScore = lowScoreGame && mostRecentWeek && 
      lowScoreGame.Year === mostRecentWeek.year && lowScoreGame.Week === mostRecentWeek.week;

    // Victory/defeat margins
    const wins = managerH2H.filter(row => row.IsWin === 1);
    const losses = managerH2H.filter(row => row.IsLoss === 1);
    
    const largestVictory = wins.length > 0 ? Math.max(...wins.map(row => row.MoV || 0)) : 0;
    const largestVictoryGame = wins.find(row => row.MoV === largestVictory);
    const hasNewLargestVictory = largestVictoryGame && mostRecentWeek && 
      largestVictoryGame.Year === mostRecentWeek.year && largestVictoryGame.Week === mostRecentWeek.week;
    
    const largestDefeat = losses.length > 0 ? Math.max(...losses.map(row => Math.abs(row.MoV || 0))) : 0;
    const largestDefeatGame = losses.find(row => Math.abs(row.MoV) === largestDefeat);
    const hasNewLargestDefeat = largestDefeatGame && mostRecentWeek && 
      largestDefeatGame.Year === mostRecentWeek.year && largestDefeatGame.Week === mostRecentWeek.week;

    const smallestVictory = wins.length > 0 ? Math.min(...wins.map(row => row.MoV || 0)) : 0;
    const smallestVictoryGame = wins.find(row => row.MoV === smallestVictory);
    const hasNewSmallestVictory = smallestVictoryGame && mostRecentWeek && 
      smallestVictoryGame.Year === mostRecentWeek.year && smallestVictoryGame.Week === mostRecentWeek.week;
    
    const smallestDefeat = losses.length > 0 ? Math.min(...losses.map(row => Math.abs(row.MoV || 0))) : 0;
    const smallestDefeatGame = losses.find(row => Math.abs(row.MoV) === smallestDefeat);
    const hasNewSmallestDefeat = smallestDefeatGame && mostRecentWeek && 
      smallestDefeatGame.Year === mostRecentWeek.year && smallestDefeatGame.Week === mostRecentWeek.week;

    // Special cases
    const mostPointsInLoss = losses.length > 0 ? Math.max(...losses.map(row => row.PF || 0)) : 0;
    const mostPointsInLossGame = losses.find(row => row.PF === mostPointsInLoss);
    const hasNewMostPointsInLoss = mostPointsInLossGame && mostRecentWeek && 
      mostPointsInLossGame.Year === mostRecentWeek.year && mostPointsInLossGame.Week === mostRecentWeek.week;

    const fewestPointsInWin = wins.length > 0 ? Math.min(...wins.map(row => row.PF || 0)) : 0;
    const fewestPointsInWinGame = wins.find(row => row.PF === fewestPointsInWin);
    const hasNewFewestPointsInWin = fewestPointsInWinGame && mostRecentWeek && 
      fewestPointsInWinGame.Year === mostRecentWeek.year && fewestPointsInWinGame.Week === mostRecentWeek.week;

    // Head-to-head records against other managers
    const h2hRecords = {};
    const allManagers = [...new Set(managerH2H.map(row => row.Manager2))];
    
    allManagers.forEach(opponent => {
      const games = managerH2H.filter(row => row.Manager2 === opponent);
      const wins = games.filter(row => row.IsWin === 1).length;
      const losses = games.filter(row => row.IsLoss === 1).length;
      const totalGames = wins + losses;
      
      h2hRecords[opponent] = {
        wins,
        losses,
        games: totalGames,
        winPct: totalGames > 0 ? (wins / totalGames * 100).toFixed(1) : '0.0'
      };
    });

    // Find best and worst matchups
    const recordsWithGames = Object.entries(h2hRecords)
      .filter(([_, record]) => record.games >= 3) // Minimum 3 games
      .map(([opponent, record]) => ({ opponent, ...record }));

    // Find best matchup, excluding Sam
    const recordsWithoutSam = recordsWithGames.filter(record => record.opponent !== 'Sam');
    const bestMatchup = recordsWithoutSam.length > 0 ? 
      recordsWithoutSam.reduce((best, current) => 
        parseFloat(current.winPct) > parseFloat(best.winPct) ? current : best
      ).opponent : '';

    const worstMatchup = recordsWithGames.length > 0 ?
      recordsWithGames.reduce((worst, current) => 
        parseFloat(current.winPct) < parseFloat(worst.winPct) ? current : worst
      ).opponent : '';

    // Find fiercest rival (most games played)
    const fiercestRival = allManagers.length > 0 ?
      allManagers.reduce((rival, current) => 
        h2hRecords[current].games > h2hRecords[rival].games ? current : rival
      ) : '';

    return {
      // Championships & Awards
      championships,
      championshipYears,
      hasNewChampionship,
      champAppearances,
      champAppearanceYears,
      hasNewChampAppearance,
      sackos,
      sackoYears,
      hasNewSacko,
      sackoAppearances,
      sackoAppearanceYears,
      hasNewSackoAppearance,

      // Team name
      currentTeamName,

      // Overall record and averages
      totalWins,
      totalLosses,
      totalGames,
      winPercentage,
      avgStanding,

      // Playoff data
      playoffAppearances,
      seasonsInLeague,
      playoffVictories,
      playoffGames,

      // Season records
      bestSeason,
      bestSeasonYears,
      hasNewBestSeason,
      worstSeason,
      worstSeasonYears,
      hasNewWorstSeason,
      highestSeasonPF,
      highestSeasonPFYears,
      hasNewHighestSeasonPF,
      lowestSeasonPF,
      lowestSeasonPFYears,
      hasNewLowestSeasonPF,
      mostSeasonWins,
      mostSeasonWinsYears,
      hasNewMostSeasonWins,
      fewestSeasonWins,
      fewestSeasonWinsYears,
      hasNewFewestSeasonWins,
      mostSeasonMoves,
      mostSeasonMovesYears,
      fewestSeasonMoves,
      fewestSeasonMovesYears,

      // Single game records
      highScore,
      highScoreGame,
      hasNewHighScore,
      lowScore,
      lowScoreGame,
      hasNewLowScore,
      largestVictory,
      largestVictoryGame,
      hasNewLargestVictory,
      largestDefeat,
      largestDefeatGame,
      hasNewLargestDefeat,
      smallestVictory,
      smallestVictoryGame,
      hasNewSmallestVictory,
      smallestDefeat,
      smallestDefeatGame,
      hasNewSmallestDefeat,
      mostPointsInLoss,
      mostPointsInLossGame,
      hasNewMostPointsInLoss,
      fewestPointsInWin,
      fewestPointsInWinGame,
      hasNewFewestPointsInWin,

      // Head-to-head
      h2hRecords,
      bestMatchup,
      worstMatchup,
      fiercestRival
    };
  };

  const stats = calculateManagerStats(selectedManager);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <div className="text-gray-600 text-lg">Loading manager data...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <div className="text-red-600 text-lg">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!h2hData.length && !finalData.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <div className="text-gray-600 text-lg">No data available</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Premium Header Section with Manager Visual Selection */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
          {/* Manager Image Hero Section */}
          <div className={`relative ${isMobile ? 'h-60' : 'h-80 lg:h-96'} bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 overflow-hidden`}>
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            
            {/* Dynamic Animated Light Effects - Now enabled on all screen sizes */}
            <div className="absolute inset-0 overflow-hidden">
              {/* Rotating light beams */}
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '8s' }}>
                <div className="absolute top-1/2 left-1/2 w-96 h-1 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent transform -translate-x-1/2 -translate-y-1/2 rotate-45"></div>
                <div className="absolute top-1/2 left-1/2 w-80 h-1 bg-gradient-to-r from-transparent via-purple-400/25 to-transparent transform -translate-x-1/2 -translate-y-1/2 rotate-135"></div>
              </div>
              
              {/* Pulsing ambient lights */}
              <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '3s' }}></div>
              <div className="absolute top-32 right-16 w-24 h-24 bg-purple-500/25 rounded-full blur-2xl animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}></div>
              <div className="absolute bottom-40 left-20 w-28 h-28 bg-cyan-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
              <div className="absolute bottom-20 right-24 w-20 h-20 bg-indigo-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDuration: '3.5s', animationDelay: '1.5s' }}></div>
              
              {/* Floating light particles */}
              <div className="absolute top-16 left-1/4 w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDuration: '2s', animationDelay: '0s' }}></div>
              <div className="absolute top-28 right-1/3 w-1.5 h-1.5 bg-cyan-300/70 rounded-full animate-bounce" style={{ animationDuration: '2.8s', animationDelay: '0.7s' }}></div>
              <div className="absolute bottom-32 left-1/3 w-1 h-1 bg-purple-300/80 rounded-full animate-bounce" style={{ animationDuration: '2.2s', animationDelay: '1.2s' }}></div>
              <div className="absolute bottom-16 right-1/4 w-2.5 h-2.5 bg-blue-200/50 rounded-full animate-bounce" style={{ animationDuration: '3.2s', animationDelay: '0.3s' }}></div>
              
              {/* Sweeping light scanner */}
              <div className="absolute inset-0">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12" style={{ animation: 'lightSweep 6s ease-in-out infinite' }}></div>
              </div>
            </div>
            
            {/* Large Manager Image - Responsive */}
            <div className="absolute inset-0 flex items-end justify-center">
              <div className="relative w-full h-full max-w-lg mx-auto flex items-end justify-center">
                {/* Premium Frame Effect with pulsing glow */}
                <div className="absolute bottom-0 w-full h-[95%] bg-gradient-to-t from-white/5 via-transparent to-transparent backdrop-blur-[2px] rounded-t-3xl"></div>
                <div className="absolute bottom-0 w-full h-[95%] border-t-2 border-l-2 border-r-2 border-white/20 rounded-t-3xl shadow-2xl animate-pulse" style={{ animationDuration: '3s' }}></div>
                
                {/* Dynamic inner glow effect with color cycling */}
                <div className="absolute bottom-0 w-full h-[95%] bg-gradient-to-t from-purple-400/10 via-blue-400/5 to-transparent rounded-t-3xl animate-pulse" style={{ animationDuration: '2s' }}></div>
                <div className="absolute bottom-0 w-full h-[95%] bg-gradient-to-t from-cyan-400/8 via-transparent to-transparent rounded-t-3xl animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '0.8s' }}></div>
                
                {/* Spotlight effect on manager */}
                <div className="absolute bottom-0 w-full h-[95%] bg-gradient-radial from-white/8 via-transparent to-transparent rounded-t-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
                
                <img
                  key={selectedManager}
                  src={`/${selectedManager.toLowerCase()}main.png`}
                  alt={selectedManager}
                  className="h-[95%] w-auto object-cover object-bottom opacity-95 relative z-10 rounded-t-3xl shadow-2xl transition-all duration-500"
                  style={{ 
                    maxWidth: '80%',
                    filter: 'drop-shadow(0 0 40px rgba(255,255,255,0.2)) drop-shadow(0 0 80px rgba(147,197,253,0.3)) brightness(1.05) contrast(1.1)',
                    animation: 'heroGlow 4s ease-in-out infinite'
                  }}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'none';
                    }
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div 
                  className="w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center relative z-10"
                  style={{ display: 'none' }}
                >
                  <User className="h-16 w-16 sm:h-24 sm:w-24 lg:h-32 lg:w-32 text-white" />
                </div>
                
                {/* Enhanced edge lighting */}
                <div className="absolute bottom-0 left-0 w-1 h-[95%] bg-gradient-to-t from-white/40 via-cyan-400/30 to-transparent rounded-tl-3xl animate-pulse" style={{ animationDuration: '2s' }}></div>
                <div className="absolute bottom-0 right-0 w-1 h-[95%] bg-gradient-to-t from-white/40 via-purple-400/30 to-transparent rounded-tr-3xl animate-pulse" style={{ animationDuration: '2s', animationDelay: '1s' }}></div>
                
                {/* Animated aura effect around manager */}
                <div className="absolute bottom-0 w-full h-[95%] border-2 border-transparent bg-gradient-to-t from-cyan-500/20 via-transparent to-transparent rounded-t-3xl animate-pulse" style={{ animationDuration: '3s', animationDelay: '0.5s' }}></div>
              </div>
            </div>
            
            {/* Manager Name Overlay - Responsive */}
            <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 text-center z-20">
              <h1 className={`${isMobile ? 'text-2xl' : 'text-4xl lg:text-6xl'} font-bold text-white drop-shadow-2xl`}>
                {selectedManager}
              </h1>
              <p className={`text-white/90 ${isMobile ? 'text-base' : 'text-xl lg:text-2xl'} font-medium mt-1 sm:mt-2 drop-shadow-lg`}>
                {stats?.currentTeamName || 'Fantasy Manager'}
              </p>
            </div>
          </div>
          
          {/* Premium Manager Selection Grid */}
          <div className="p-3 sm:p-6 lg:p-8">
            <div className="mb-4 sm:mb-6">
              <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900 mb-2`}>Select Manager</h3>
              <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>Choose a manager to view their complete profile and statistics</p>
            </div>
            
            {/* Manager Grid Selector - Responsive */}
            <div className={`grid ${isMobile ? 'grid-cols-3 gap-2' : 'grid-cols-4 lg:grid-cols-7 xl:grid-cols-13 gap-3 lg:gap-4'}`}>
              {managers.map((manager) => (
                <button
                  key={manager}
                  onClick={() => setSelectedManager(manager)}
                  className={`group relative ${isMobile ? 'p-2' : 'p-3'} rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-105 ${
                    selectedManager === manager
                      ? 'bg-gradient-to-br from-purple-500 to-blue-600 shadow-xl ring-4 ring-purple-200'
                      : 'bg-gray-100 hover:bg-gray-200 shadow-md hover:shadow-lg'
                  }`}
                >
                  {/* Manager Mini Image */}
                  <div className="relative mb-1 sm:mb-2">
                    <div className={`${isMobile ? 'w-8 h-8' : 'w-12 h-12 lg:w-14 lg:h-14'} rounded-full overflow-hidden mx-auto shadow-lg ring-2 ring-white`}>
                      <img
                        key={manager}
                        src={`/${manager.toLowerCase()}main.png`}
                        alt={manager}
                        className="w-full h-full object-cover"
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'none';
                          }
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div 
                        className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center"
                        style={{ display: 'none' }}
                      >
                        <User className={`${isMobile ? 'h-4 w-4' : 'h-6 w-6'} text-white`} />
                      </div>
                    </div>
                    
                    {/* Selection indicator */}
                    {selectedManager === manager && (
                      <div className={`absolute ${isMobile ? '-top-0.5 -right-0.5 w-3 h-3' : '-top-1 -right-1 w-4 h-4'} bg-green-400 rounded-full border-2 border-white shadow-sm`}>
                        <div className="w-full h-full bg-green-400 rounded-full animate-ping"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Manager Name */}
                  <div className="text-center">
                    <span className={`${isMobile ? 'text-xs' : 'text-xs lg:text-sm'} font-bold transition-colors ${
                      selectedManager === manager
                        ? 'text-white'
                        : 'text-gray-700 group-hover:text-gray-900'
                    }`}>
                      {manager}
                    </span>
                  </div>
                  
                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-500/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              ))}
            </div>
            
            {/* Quick Stats Preview - Responsive */}
            {stats && (
              <div className={`mt-4 sm:mt-6 ${isMobile ? 'p-3' : 'p-4'} bg-gray-50 rounded-xl border border-gray-200`}>
                <div className={`flex items-center justify-between ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  <div className={`flex items-center ${isMobile ? 'space-x-3' : 'space-x-6'}`}>
                    <div className="text-center">
                      <div className="font-bold text-gray-900">{stats.totalWins}-{stats.totalLosses}</div>
                      <div className="text-gray-600">Record</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900">{stats.winPercentage}%</div>
                      <div className="text-gray-600">Win Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900">{stats.championships}</div>
                      <div className="text-gray-600">Championships</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">#{stats.avgStanding}</div>
                    <div className="text-gray-600">Avg Finish</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards Grid - Mobile Optimized */}
        {stats && (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            {/* Row 1: Championships & Awards */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6'}`}>
              <ChampionshipCard
                title="Championships"
                value={stats.championships}
                subtitle={stats.championshipYears.length > 0 ? stats.championshipYears.join(', ') : 'None yet'}
                icon={Trophy}
                isNew={stats.hasNewChampionship}
                selectedManager={selectedManager}
                isMobile={isMobile}
              />
              <ChampionshipCard
                title="Championship Appearances"
                value={stats.champAppearances}
                subtitle={stats.champAppearanceYears.length > 0 ? stats.champAppearanceYears.join(', ') : 'None yet'}
                icon={Crown}
                isSecondary={true}
                isNew={stats.hasNewChampAppearance}
                selectedManager={selectedManager}
                isMobile={isMobile}
              />
              <SackoCard
                title="Sackos"
                value={stats.sackos}
                subtitle={stats.sackoYears.length > 0 ? stats.sackoYears.join(', ') : 'None yet'}
                isNew={stats.hasNewSacko}
                selectedManager={selectedManager}
                isMobile={isMobile}
              />
              <SackoCard
                title="Sacko Appearances"
                value={stats.sackoAppearances}
                subtitle={stats.sackoAppearanceYears.length > 0 ? stats.sackoAppearanceYears.join(', ') : 'None yet'}
                isSecondary={true}
                isNew={stats.hasNewSackoAppearance}
                selectedManager={selectedManager}
                isMobile={isMobile}
              />
            </div>

            {/* Row 2: Playoff Performance */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6'}`}>
              <CleanStatCard
                title="Playoff Appearances"
                value={stats.playoffAppearances}
                subtitle={`in ${stats.seasonsInLeague} seasons with the League`}
                color="blue"
                isMobile={isMobile}
              />
              <CleanStatCard
                title="Playoff Victories"
                value={stats.playoffVictories}
                subtitle={`in ${stats.playoffGames} games played`}
                color="purple"
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Most Season Wins"
                value={stats.mostSeasonWins}
                gameInfo={{ YearWeek: stats.mostSeasonWinsYears.join(', ') }}
                color="blue"
                isNew={stats.hasNewMostSeasonWins}
                isSeasonRecord={true}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Fewest Season Wins"
                value={stats.fewestSeasonWins}
                gameInfo={{ YearWeek: stats.fewestSeasonWinsYears.join(', ') }}
                color="gray"
                isNew={stats.hasNewFewestSeasonWins}
                isSeasonRecord={true}
                isMobile={isMobile}
              />
            </div>

            {/* Row 3: Season Scoring Records */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6'}`}>
              <CleanGameStatCard
                title="Highest Season PF"
                value={stats.highestSeasonPF?.toFixed(2)}
                gameInfo={{ YearWeek: stats.highestSeasonPFYears.join(', ') }}
                color="green"
                isNew={stats.hasNewHighestSeasonPF}
                isSeasonRecord={true}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Lowest Season PF"
                value={stats.lowestSeasonPF?.toFixed(2)}
                gameInfo={{ YearWeek: stats.lowestSeasonPFYears.join(', ') }}
                color="red"
                isNew={stats.hasNewLowestSeasonPF}
                isSeasonRecord={true}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Most Season Moves"
                value={stats.mostSeasonMoves}
                gameInfo={{ YearWeek: stats.mostSeasonMovesYears.join(', ') }}
                color="orange"
                isSeasonRecord={true}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Fewest Season Moves"
                value={stats.fewestSeasonMoves}
                gameInfo={{ YearWeek: stats.fewestSeasonMovesYears.join(', ') }}
                color="yellow"
                isSeasonRecord={true}
                isMobile={isMobile}
              />
            </div>

            {/* Row 4: Single Game Records */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6'}`}>
              <CleanGameStatCard
                title="High Score"
                value={stats.highScore?.toFixed(2)}
                gameInfo={stats.highScoreGame}
                color="green"
                isNew={stats.hasNewHighScore}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Low Score"
                value={stats.lowScore?.toFixed(2)}
                gameInfo={stats.lowScoreGame}
                color="red"
                isNew={stats.hasNewLowScore}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Most Points in Loss"
                value={stats.mostPointsInLoss?.toFixed(2)}
                gameInfo={stats.mostPointsInLossGame}
                color="orange"
                isNew={stats.hasNewMostPointsInLoss}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Fewest Points in Win"
                value={stats.fewestPointsInWin?.toFixed(2)}
                gameInfo={stats.fewestPointsInWinGame}
                color="yellow"
                isNew={stats.hasNewFewestPointsInWin}
                isMobile={isMobile}
              />
            </div>

            {/* Row 5: Victory/Defeat Margins */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6'}`}>
              <CleanGameStatCard
                title="Largest Victory"
                value={`+${stats.largestVictory?.toFixed(2)}`}
                gameInfo={stats.largestVictoryGame}
                color="green"
                isNew={stats.hasNewLargestVictory}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Largest Defeat"
                value={`-${stats.largestDefeat?.toFixed(2)}`}
                gameInfo={stats.largestDefeatGame}
                color="red"
                isNew={stats.hasNewLargestDefeat}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Smallest Victory"
                value={`+${stats.smallestVictory?.toFixed(2)}`}
                gameInfo={stats.smallestVictoryGame}
                color="blue"
                isNew={stats.hasNewSmallestVictory}
                isMobile={isMobile}
              />
              <CleanGameStatCard
                title="Smallest Defeat"
                value={`-${stats.smallestDefeat?.toFixed(2)}`}
                gameInfo={stats.smallestDefeatGame}
                color="purple"
                isNew={stats.hasNewSmallestDefeat}
                isMobile={isMobile}
              />
            </div>

            {/* Row 6: Head-to-Head Rivalries */}
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6'}`}>
              <CleanRivalryCard
                title={stats.bestMatchup}
                subtitle={`is owned by ${selectedManager}`}
                description={`${selectedManager} dominates ${stats.bestMatchup} with a record of ${stats.h2hRecords[stats.bestMatchup]?.wins || 0}-${stats.h2hRecords[stats.bestMatchup]?.losses || 0} (${stats.h2hRecords[stats.bestMatchup]?.winPct || 0}%) in their ${stats.h2hRecords[stats.bestMatchup]?.games || 0} meetings.`}
                color="green"
                isMobile={isMobile}
              />
              <CleanRivalryCard
                title={stats.worstMatchup}
                subtitle={`has ${selectedManager}'s number`}
                description={`${selectedManager} struggles against ${stats.worstMatchup} with a record of ${stats.h2hRecords[stats.worstMatchup]?.wins || 0}-${stats.h2hRecords[stats.worstMatchup]?.losses || 0} (${stats.h2hRecords[stats.worstMatchup]?.winPct || 0}%) in their ${stats.h2hRecords[stats.worstMatchup]?.games || 0} meetings.`}
                color="red"
                isMobile={isMobile}
              />
              <CleanRivalryCard
                title={stats.fiercestRival}
                subtitle={`is ${selectedManager}'s fiercest rival`}
                description={`${selectedManager} and ${stats.fiercestRival} have battled ${stats.h2hRecords[stats.fiercestRival]?.games || 0} times, with ${selectedManager} holding a ${stats.h2hRecords[stats.fiercestRival]?.wins || 0}-${stats.h2hRecords[stats.fiercestRival]?.losses || 0} record (${stats.h2hRecords[stats.fiercestRival]?.winPct || 0}%) in this heated rivalry.`}
                color="purple"
                isMobile={isMobile}
              />
            </div>

            {/* Charts Section - Responsive */}
            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              <StandingsHistoryChart 
                finalData={finalData}
                selectedManager={selectedManager}
              />
              <PointScoringHistoryChart 
                h2hData={h2hData}
                selectedManager={selectedManager}
              />
              <PointsAgainstHistoryChart 
                h2hData={h2hData}
                selectedManager={selectedManager}
              />
              <MarginOfVictoryChart 
                h2hData={h2hData}
                selectedManager={selectedManager}
              />
              <HeadToHeadPerformanceChart 
                h2hData={h2hData}
                selectedManager={selectedManager}
              />
              <TopInvestmentsChart 
                draftData={draftData}
                selectedManager={selectedManager}
              />
              <DraftTendenciesChart 
                selectedManager={selectedManager}
                draftData={draftData}
              />
              <LegacyScoreChart 
                selectedManager={selectedManager}
                legacyData={legacyData}
                finalData={finalData}
                h2hData={h2hData}
              />
              <LegacyRankChart 
                selectedManager={selectedManager}
                legacyData={legacyData}
                finalData={finalData}
                h2hData={h2hData}
              />
              <IndividualTeamsLegacyChart 
                selectedManager={selectedManager}
                finalData={finalData}
                h2hData={h2hData}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Championship Card with prominent trophy icon and floating champion image
const ChampionshipCard = ({ title, value, subtitle, icon: Icon, isSecondary = false, isNew = false, selectedManager, isMobile = false }) => {
  // Determine which championship image to use based on value
  const getChampionshipImageName = (champCount) => {
    if (champCount === 1) return `${selectedManager.toLowerCase()}champ.png`;
    if (champCount === 2) return `${selectedManager.toLowerCase()}champ2.png`;
    if (champCount === 3) return `${selectedManager.toLowerCase()}champ3.png`;
    if (champCount === 4) return `${selectedManager.toLowerCase()}champ4.png`;
    if (champCount === 5) return `${selectedManager.toLowerCase()}champ5.png`;
    if (champCount >= 6) return `${selectedManager.toLowerCase()}champ6.png`;
    return null;
  };

  const championshipImageSrc = getChampionshipImageName(value);

  return (
    <div className={`${isSecondary ? 'bg-gradient-to-br from-amber-400 via-orange-500 to-red-500' : 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600'} rounded-xl sm:rounded-2xl shadow-xl ${isMobile ? 'p-4' : 'p-6'} text-white relative overflow-hidden transform hover:scale-105 transition-all duration-300 ${isNew ? 'ring-4 ring-white/50 ring-offset-2 ring-offset-transparent' : ''}`}>
      <div className="absolute inset-0 bg-black/10"></div>
      
      {/* Full card champion image - only for "Championships" title and if they have championships */}
      {title === "Championships" && value > 0 && championshipImageSrc && (
        <div className="absolute inset-2 rounded-xl overflow-hidden z-20 flex items-end justify-end">
          <img
            key={`${selectedManager}-champ-${value}`}
            src={`/${championshipImageSrc}`}
            alt={`${selectedManager} Champion (${value} championships)`}
            className="h-full object-contain opacity-50 hover:opacity-75 transition-opacity duration-300"
            onError={(e) => {
              // Fallback to default champion image if specific count image doesn't exist
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          {/* Fallback to default champion image */}
          <img
            key={`${selectedManager}-champ-fallback`}
            src={`/${selectedManager.toLowerCase()}champ.png`}
            alt={`${selectedManager} Champion`}
            className="h-full object-contain opacity-50 hover:opacity-75 transition-opacity duration-300"
            style={{ display: 'none' }}
            onError={(e) => {
              // Hide if no champion image exists at all
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      
      {isNew && (
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 z-20">
          <div className="bg-white/90 text-gray-900 text-xs font-bold px-2 py-1 rounded-full animate-pulse">
            NEW!
          </div>
        </div>
      )}
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className={`${isMobile ? 'p-2' : 'p-3'} rounded-xl bg-white/20 backdrop-blur-sm`}>
            {/* Use thebeastcrown.png for championship cards, fallback to icon */}
            <img
              src="/thebeastcrown.png"
              alt="Championship Crown"
              className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} object-contain`}
              onError={(e) => {
                // Fallback to icon if thebeastcrown.png doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Icon className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-white`} style={{ display: 'none' }} />
          </div>
        </div>
        
        <div className="space-y-2">
          <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold uppercase tracking-wide ${title === "Championships" ? 'text-white font-black' : 'text-white/90 font-medium'} drop-shadow-lg`}>
            {title}
          </h3>
          <div className={`${isMobile ? 'text-2xl' : 'text-3xl lg:text-4xl'} font-bold ${title === "Championships" ? 'font-black' : 'font-bold'} text-white drop-shadow-lg`}>
            {value}
          </div>
          {subtitle && (
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} ${title === "Championships" ? 'text-white font-semibold' : 'text-white/80'} drop-shadow-md`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Sacko Card with poop emoji and conditional background images
const SackoCard = ({ title, value, subtitle, isSecondary = false, isNew = false, selectedManager, isMobile = false }) => {
  // Determine which sacko image to use based on value
  const getSackoImageName = (sackoCount) => {
    if (sackoCount === 1) return `${selectedManager.toLowerCase()}anguished.png`;
    if (sackoCount === 2) return `${selectedManager.toLowerCase()}sacko2.png`;
    if (sackoCount >= 3) return `${selectedManager.toLowerCase()}sacko3.png`;
    return null;
  };

  const sackoImageSrc = getSackoImageName(value);

  return (
    <div className={`${isSecondary ? 'bg-gradient-to-br from-red-600 via-red-700 to-red-800' : 'bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800'} rounded-xl sm:rounded-2xl shadow-xl ${isMobile ? 'p-4' : 'p-6'} text-white relative overflow-hidden transform hover:scale-105 transition-all duration-300 ${isNew ? 'ring-4 ring-red-500/50 ring-offset-2 ring-offset-transparent' : ''}`}>
      <div className="absolute inset-0 bg-black/10"></div>
      
      {/* Sacko background image - only for "Sackos" title and if they have sackos */}
      {title === "Sackos" && value > 0 && sackoImageSrc && (
        <div className="absolute inset-2 rounded-xl overflow-hidden z-10 flex items-end justify-end opacity-50">
          <img
            key={`${selectedManager}-sacko-${value}`}
            src={`/${sackoImageSrc}`}
            alt={`${selectedManager} Sacko`}
            className="h-[85%] object-contain hover:opacity-75 transition-opacity duration-300"
            onError={(e) => {
              // Hide if no sacko image exists
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      
      {isNew && (
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 z-30">
          <div className="bg-red-100 text-red-900 text-xs font-bold px-2 py-1 rounded-full animate-pulse">
            NEW!
          </div>
        </div>
      )}
      <div className="relative z-20">
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className={`${isMobile ? 'p-2' : 'p-3'} rounded-xl bg-white/10 backdrop-blur-sm`}>
            {/* Use sackocap.png for sacko cards, fallback to poop emoji */}
            <img
              src="/sackocap.png"
              alt="Sacko Cap"
              className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} object-contain`}
              onError={(e) => {
                // Fallback to poop emoji if sackocap.png doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className={`${isMobile ? 'text-xl' : 'text-2xl'}`} style={{ display: 'none' }}>💩</div>
          </div>
        </div>
        
        <div className="space-y-2">
          <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold uppercase tracking-wide ${title === "Sackos" ? 'text-white font-black' : 'text-white/90 font-medium'} drop-shadow-lg`}>
            {title}
          </h3>
          <div className={`${isMobile ? 'text-2xl' : 'text-3xl lg:text-4xl'} font-bold ${title === "Sackos" ? 'font-black' : 'font-bold'} text-white drop-shadow-lg`}>
            {value}
          </div>
          {subtitle && (
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} ${title === "Sackos" ? 'text-white font-semibold' : 'text-white/80'} drop-shadow-md`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Clean StatCard without prominent icons
const CleanStatCard = ({ title, value, subtitle, color, isMobile = false }) => {
  const colorClasses = {
    purple: 'border-purple-500/20 bg-slate-800',
    blue: 'border-blue-500/20 bg-slate-800',
    green: 'border-green-500/20 bg-slate-800',
    orange: 'border-orange-500/20 bg-slate-800',
    red: 'border-red-500/20 bg-slate-800',
    yellow: 'border-yellow-500/20 bg-slate-800',
    gray: 'border-gray-500/20 bg-slate-800'
  };

  const accentColors = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    gray: 'bg-gray-500'
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-xl sm:rounded-2xl shadow-xl ${isMobile ? 'p-4' : 'p-6'} text-white hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="space-y-2 sm:space-y-3">
        <div className="flex items-start justify-between">
          <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-300 uppercase tracking-wide`}>
            {title}
          </h3>
          <div className={`w-3 h-3 rounded-full ${accentColors[color]}`}></div>
        </div>
        <div className={`${isMobile ? 'text-lg' : 'text-xl lg:text-2xl'} font-bold text-white`}>
          {value}
        </div>
        {subtitle && (
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

// Clean GameStatCard without prominent icons
const CleanGameStatCard = ({ title, value, gameInfo, color, isNew = false, isSeasonRecord = false, isMobile = false }) => {
  const colorClasses = {
    purple: 'border-purple-500/20 bg-slate-800',
    blue: 'border-blue-500/20 bg-slate-800',
    green: 'border-green-500/20 bg-slate-800',
    orange: 'border-orange-500/20 bg-slate-800',
    red: 'border-red-500/20 bg-slate-800',
    yellow: 'border-yellow-500/20 bg-slate-800',
    gray: 'border-gray-500/20 bg-slate-800'
  };

  const accentColors = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    gray: 'bg-gray-500'
  };

  const newRingColors = {
    purple: 'ring-purple-500/50',
    blue: 'ring-blue-500/50',
    green: 'ring-green-500/50',
    orange: 'ring-orange-500/50',
    red: 'ring-red-500/50',
    yellow: 'ring-yellow-500/50',
    gray: 'ring-gray-500/50'
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-xl sm:rounded-2xl shadow-xl ${isMobile ? 'p-4' : 'p-6'} text-white hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative ${isNew ? `ring-4 ${newRingColors[color]} ring-offset-2 ring-offset-transparent` : ''}`}>
      {isNew && (
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3 z-20">
          <div className="bg-white/90 text-gray-900 text-xs font-bold px-2 py-1 rounded-full animate-pulse">
            NEW!
          </div>
        </div>
      )}
      <div className="space-y-2 sm:space-y-3">
        <div className="flex items-start justify-between">
          <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-300 uppercase tracking-wide`}>
            {title}
          </h3>
          <div className={`w-3 h-3 rounded-full ${accentColors[color]}`}></div>
        </div>
        <div className={`${isMobile ? 'text-lg' : 'text-xl lg:text-2xl'} font-bold text-white`}>
          {value}
        </div>
        {gameInfo && (
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>
            {isSeasonRecord ? (
              // For season records, just show the year
              gameInfo.YearWeek
            ) : (
              // For single game records, show full game details
              <>
                {gameInfo.YearWeek} vs. {gameInfo.Manager2}
                {gameInfo.PF && gameInfo.PA && (
                  <span className="block">{gameInfo.PF?.toFixed(2)} - {gameInfo.PA?.toFixed(2)}</span>
                )}
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

// Clean RivalryCard without prominent icons
const CleanRivalryCard = ({ title, subtitle, description, color, isMobile = false }) => {
  const colorClasses = {
    green: 'border-green-500/20 bg-gradient-to-br from-green-900/80 via-slate-800 to-slate-900',
    red: 'border-red-500/20 bg-gradient-to-br from-red-900/80 via-slate-800 to-slate-900',
    purple: 'border-purple-500/20 bg-gradient-to-br from-purple-900/80 via-slate-800 to-slate-900'
  };

  const accentColors = {
    green: 'bg-green-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-xl sm:rounded-2xl shadow-xl ${isMobile ? 'p-4' : 'p-6'} text-white hover:shadow-2xl transition-all duration-300 relative overflow-hidden`}>
      {/* Rival manager image in bottom-right */}
      {title && (
        <div className="absolute bottom-0 right-0 z-20 hover:z-50 transition-all duration-300 group">
          {/* Lighting effects behind the manager image */}
          <div className="absolute inset-0 -m-2">
            {/* Pulsing glow effect */}
            <div className="absolute inset-0 bg-gradient-radial from-white/20 via-white/5 to-transparent rounded-lg animate-pulse" style={{ animationDuration: '3s' }}></div>
            
            {/* Color-specific ambient glow */}
            <div className={`absolute inset-0 rounded-lg animate-pulse ${
              color === 'green' ? 'bg-gradient-radial from-green-400/30 via-green-400/10 to-transparent' :
              color === 'red' ? 'bg-gradient-radial from-red-400/30 via-red-400/10 to-transparent' :
              'bg-gradient-radial from-purple-400/30 via-purple-400/10 to-transparent'
            }`} style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}></div>
            
            {/* Edge lighting */}
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/40 to-transparent animate-pulse" style={{ animationDuration: '2s' }}></div>
            <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-white/40 to-transparent animate-pulse" style={{ animationDuration: '2s', animationDelay: '1s' }}></div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-white/40 to-transparent animate-pulse" style={{ animationDuration: '2.2s', animationDelay: '0.3s' }}></div>
            
            {/* Floating light particles around the manager */}
            <div className="absolute top-2 left-2 w-1 h-1 bg-white/80 rounded-full animate-bounce" style={{ animationDuration: '2s', animationDelay: '0s' }}></div>
            <div className="absolute top-4 right-3 w-0.5 h-0.5 bg-cyan-300/90 rounded-full animate-bounce" style={{ animationDuration: '2.3s', animationDelay: '0.7s' }}></div>
            <div className="absolute bottom-6 left-1 w-0.5 h-0.5 bg-purple-300/80 rounded-full animate-bounce" style={{ animationDuration: '1.8s', animationDelay: '1.1s' }}></div>
          </div>
          
          <div className={`${isMobile ? 'w-16 h-20' : 'w-20 h-24 lg:w-24 lg:h-28'} overflow-hidden relative`}>
            {/* Enhanced glow filter on hover */}
            <img
              key={`${title}-rival`}
              src={`/${title.toLowerCase()}main.png`}
              alt={title}
              className="w-full h-full object-cover object-top opacity-60 group-hover:opacity-100 transition-all duration-300 shadow-lg group-hover:shadow-2xl relative z-10"
              style={{
                filter: 'brightness(1.2) contrast(1.1) drop-shadow(0 0 10px rgba(255,255,255,0.3))',
                transition: 'filter 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.filter = 'brightness(1.3) contrast(1.15) drop-shadow(0 0 20px rgba(255,255,255,0.5)) drop-shadow(0 0 30px rgba(147,197,253,0.4))';
              }}
              onMouseLeave={(e) => {
                e.target.style.filter = 'brightness(1.2) contrast(1.1) drop-shadow(0 0 10px rgba(255,255,255,0.3))';
              }}
              onError={(e) => {
                // Hide if no manager image exists
                e.target.style.display = 'none';
              }}
            />
            
            {/* Animated border glow */}
            <div className="absolute inset-0 border-2 border-transparent bg-gradient-to-t from-white/20 via-transparent to-transparent rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>
      )}
      
      <div className={`space-y-2 sm:space-y-3 relative z-10 ${isMobile ? 'pr-16' : 'pr-24 lg:pr-28'}`}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-white`}>{title}</h3>
            <p className={`${isMobile ? 'text-sm' : 'text-sm'} text-gray-300`}>{subtitle}</p>
          </div>
          <div className={`w-3 h-3 rounded-full ${accentColors[color]} mt-1`}></div>
        </div>
        <p className={`${isMobile ? 'text-sm' : 'text-sm'} text-gray-400 leading-relaxed`}>
          {description}
        </p>
      </div>
    </div>
  );
};

export default ManagerCards;